import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./apiClient";
import type { Project } from "@/server/types";

export const useProjects = () =>
  useQuery({
    queryKey: ["projects"],
    queryFn: () => api.listProjects(),
    refetchInterval: 10000,
  });

export const useProject = (id: string | undefined) =>
  useQuery({
    queryKey: ["project", id],
    queryFn: () => api.getProject({ id: id! }),
    enabled: !!id,
  });

export const useCreateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      name: string;
      githubUrl: string;
      workingDir: string;
      jiraUrl?: string;
      defaultTask?: string;
      defaultNetwork?: string;
    }) => api.createProject(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: Parameters<typeof api.updateProject>[0]) =>
      api.updateProject(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteProject({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
};

export const usePollJira = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) => api.pollJiraTickets({ projectId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instances"] });
    },
  });
};
